import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import UserTable from "./components/UserTable";
import CreateUserModal from "./components/CreateUserModal";
import EnrollClassPanel, {
  EnrollClassSelections,
} from "./components/EnrollClassPanel";
import FaceEnrollCard from "views/admin/profile/components/FaceEnrollCard";
import { userService } from "services/admin/userService";
import { centerService } from "services/admin/centerService";
import { classMgmtService } from "services/admin/classMgmtService";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import type {
  UserInviteCreate,
  UserListItem,
  UserRole,
  CenterListItem,
} from "types/domain";

const VALID_ROLES: UserRole[] = [
  "SYSTEM_ADMIN",
  "MANAGER",
  "TRAINER",
  "TEACHER",
  "STUDENT",
];

const ROLE_LABEL_KEYS: Record<UserRole, string> = {
  SYSTEM_ADMIN: "enum.role.SYSTEM_ADMIN",
  MANAGER: "enum.role.MANAGER",
  TRAINER: "enum.roleShort.TRAINER",
  TEACHER: "enum.role.TEACHER",
  STUDENT: "enum.role.STUDENT",
};

// Renders a translated template with its {placeholders} in bold, so word
// order stays under the translator's control instead of being hardcoded.
function renderBold(template: string, values: Record<string, string>) {
  return template.split(/(\{\w+\})/g).map((part, i) => {
    const match = part.match(/^\{(\w+)\}$/);
    if (match && match[1] in values) {
      return <strong key={i}>{values[match[1]]}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function normalizeUser(item: unknown): UserListItem | null {
  if (!item || typeof item !== "object") return null;
  const raw = item as Partial<UserListItem> & { role?: string };

  if (!raw.id || !raw.full_name || !raw.role) return null;
  if (!VALID_ROLES.includes(raw.role as UserRole)) return null;

  return {
    id: String(raw.id),
    username: raw.username ?? null,
    full_name: String(raw.full_name),
    email: raw.email ?? null,
    phone: raw.phone ?? null,
    role: raw.role as UserRole,
    is_active: Boolean(raw.is_active),
    code: raw.code ?? null,
    avatar_url: raw.avatar_url ?? null,
    center_id: raw.center_id ?? null,
    center_name: raw.center_name ?? null,
    dob: raw.dob ?? null,
    face_count: (raw as any).face_count ?? 0,
    is_first_login: (raw as any).is_first_login ?? false,
    trial_end_date: (raw as any).trial_end_date ?? null,
    updated_at: (raw as any).updated_at ?? null,
    can_delete: (raw as any).can_delete ?? null,
  };
}

const UsersManagement = ({
  centerId: centerIdProp,
}: { centerId?: string } = {}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = React.useState<UserListItem[]>([]);
  const [centers, setCenters] = React.useState<CenterListItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [centersLoading, setCentersLoading] = React.useState(true);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [currentUserCenterId, setCurrentUserCenterId] = React.useState<
    string | null
  >(centerIdProp ?? null);
  const [currentUserRole, setCurrentUserRole] = React.useState<UserRole | null>(
    null
  );

  // Read filters from URL search params so they persist across navigation
  const selectedRole: UserRole | "ALL" =
    (searchParams.get("role") as UserRole | null) ?? "ALL";
  const selectedCenterId: string | "ALL" = searchParams.get("center") ?? "ALL";
  const searchQuery = searchParams.get("q") ?? "";
  // Extract primitives so useCallback deps don't get a new object reference each render,
  // which would otherwise cause fetchUsers to recreate and fire in an infinite loop.
  const paginationPage = Number(searchParams.get("page")) || 1;
  const paginationLimit = Number(searchParams.get("limit")) || 500;

  const setSelectedRole = React.useCallback(
    (role: UserRole | "ALL") => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (role === "ALL") next.delete("role");
          else next.set("role", role);
          next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSelectedCenterId = React.useCallback(
    (centerId: string | "ALL") => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (centerId === "ALL") next.delete("center");
          else next.set("center", centerId);
          next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSearchQuery = React.useCallback(
    (q: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!q.trim()) next.delete("q");
          else next.set("q", q);
          next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  React.useEffect(() => {
    if (centerIdProp !== undefined) {
      setCurrentUserCenterId(centerIdProp);
      setCurrentUserRole("SYSTEM_ADMIN");
      return;
    }
    const stored = sessionStorage.getItem("current_user");
    if (stored) {
      try {
        const user = JSON.parse(stored);
        setCurrentUserCenterId(
          user.role === "SYSTEM_ADMIN" ? null : user.center_id || null
        );
        setCurrentUserRole(user.role || null);
      } catch (e) {
        setCurrentUserRole(null);
      }
    }
  }, [centerIdProp]);

  React.useEffect(() => {
    if (currentUserRole !== "SYSTEM_ADMIN") {
      setCenters([]);
      setCentersLoading(false);
      return;
    }
    const fetchCenters = async () => {
      setCentersLoading(true);
      try {
        const response = await centerService.getAll({
          skip: 0,
          limit: 200,
          include_manager: false,
        });
        setCenters(response.items);
      } catch (err) {
        console.error("Failed to load centers:", err);
        setCenters([]);
      } finally {
        setCentersLoading(false);
      }
    };
    fetchCenters();
  }, [currentUserRole]);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      let centerId: string | null = null;
      if (currentUserCenterId) {
        centerId = currentUserCenterId;
      } else if (selectedCenterId !== "ALL") {
        centerId = selectedCenterId;
      }

      const queryParams: any = {
        center_id: centerId,
        page: paginationPage,
        limit: paginationLimit,
      };
      if (selectedRole !== "ALL") queryParams.role = selectedRole;
      if (searchQuery.trim()) queryParams.search = searchQuery.trim();

      const response = await userService.getAll(queryParams);

      let normalizedUsers = response.items
        .map(normalizeUser)
        .filter((user): user is UserListItem => user !== null);

      if (currentUserRole === "TEACHER" || currentUserRole === "TRAINER") {
        normalizedUsers = normalizedUsers.filter((u) => u.role !== "MANAGER");
      }

      setUsers(
        [...normalizedUsers].sort((a, b) =>
          (a.full_name || "").localeCompare(b.full_name || "", "vi", {
            sensitivity: "base",
          })
        )
      );
    } catch (err) {
      console.error("Failed to load users:", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [
    currentUserCenterId,
    currentUserRole,
    selectedCenterId,
    selectedRole,
    searchQuery,
    paginationPage,
    paginationLimit,
  ]);

  // Read the active-state filter from the URL so it survives navigation.
  const activeFilterParam = searchParams.get("active");
  const activeFilter: "all" | "active" | "inactive" =
    activeFilterParam === "active" || activeFilterParam === "inactive"
      ? activeFilterParam
      : "all";
  const setActiveFilter = React.useCallback(
    (value: "all" | "active" | "inactive") => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "all") next.delete("active");
          else next.set("active", value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Apply lightweight client-side filters as a UI fallback. The list endpoint
  // is still queried with search/role/center, but this keeps search responsive
  // and also covers accent-insensitive matches and extra fields (email, phone,
  // username, code, center_name) the backend's name-only search doesn't cover.
  const visibleUsers = React.useMemo(() => {
    const wantActive = activeFilter === "active";
    const normalizeText = (value: string) =>
      value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    const normalizedQuery = normalizeText(searchQuery.trim());

    return users.filter((user) => {
      if (activeFilter !== "all" && (user.is_active ?? true) !== wantActive) {
        return false;
      }
      if (!normalizedQuery) return true;

      return normalizeText(
        [
          user.full_name,
          user.email,
          user.phone,
          user.username,
          user.code,
          user.center_name,
        ]
          .filter(Boolean)
          .join(" ")
      ).includes(normalizedQuery);
    });
  }, [users, activeFilter, searchQuery]);

  React.useEffect(() => {
    if (currentUserRole === "SYSTEM_ADMIN") {
      fetchUsers();
      return;
    }
    if (currentUserCenterId && currentUserRole) {
      fetchUsers();
      return;
    }
    if (currentUserRole) {
      setLoading(false);
      setUsers([]);
    }
  }, [currentUserCenterId, currentUserRole, fetchUsers]);

  // ── Password modal state ──
  const [passwordModal, setPasswordModal] = React.useState<{
    userId: string;
  } | null>(null);
  const [newPassword, setNewPassword] = React.useState("");
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = React.useState(false);

  // ── Role change modal state ──
  const [roleChangeModal, setRoleChangeModal] = React.useState<{
    userId: string;
    newRole: string;
    userName: string;
  } | null>(null);
  const [roleChangeSubmitting, setRoleChangeSubmitting] = React.useState(false);
  const [roleChangeError, setRoleChangeError] = React.useState<string | null>(
    null
  );
  // Bumped on cancel so RoleSelectCell resets its pending state (C3)
  const [roleChangeResetToken, setRoleChangeResetToken] = React.useState(0);

  // ── C1: In-app toggle-active confirmation modal ──
  const [toggleModal, setToggleModal] = React.useState<{
    userId: string;
    userName: string;
    currentlyActive: boolean;
  } | null>(null);
  const [toggleSubmitting, setToggleSubmitting] = React.useState(false);
  const [toggleError, setToggleError] = React.useState<string | null>(null);

  // ── C1: In-app delete confirmation modal ──
  const [deleteModal, setDeleteModal] = React.useState<{
    userId: string;
    userName: string;
  } | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  // ── Bulk delete confirmation modal ──
  const [bulkDeleteModal, setBulkDeleteModal] = React.useState<{
    userIds: string[];
  } | null>(null);
  const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = React.useState(false);
  const [bulkDeleteError, setBulkDeleteError] = React.useState<string | null>(
    null
  );

  const getUserProfilePath = React.useCallback(
    (userId: string) => {
      if (location.pathname.startsWith("/systemadmin")) {
        return `/systemadmin/users-management/${userId}/profile`;
      }
      return `/admin/users/${userId}/profile`;
    },
    [location.pathname]
  );

  const [faceEnrollUserId, setFaceEnrollUserId] = React.useState<string | null>(
    null
  );
  const [faceEnrollComplete, setFaceEnrollComplete] = React.useState(false);
  // Carries the freshly-created student's name from handleCreate through
  // the face-enrollment modal so the wizard can greet them by name.
  const pendingCreated = React.useRef<{
    fullName: string;
    role: UserRole | null;
  }>({ fullName: "", role: null });
  // Wizard shown right after a fresh student has finished face enrollment —
  // lets the manager enroll them into one or more classes (and optionally
  // sync them into upcoming sessions) in a couple of clicks.
  // Ref into the inline EnrollClassPanel inside the face-enrollment modal
  // so the footer's "Hoàn tất" button can read the current selections
  // without forcing the modal to re-render on every checkbox toggle.
  const enrollPanelRef = React.useRef<{
    getSelections: () => EnrollClassSelections;
  } | null>(null);
  const [enrollSaving, setEnrollSaving] = React.useState(false);
  // Tracks whether the EnrollClassPanel currently has any class/session
  // selected so the parent can collapse the panel back into single-column
  // mode when the user un-selects everything. Defaults to true so the
  // picker is visible the moment a STUDENT finishes face enrollment,
  // without forcing the user to toggle a class first.
  const [enrollHasSelection, setEnrollHasSelection] = React.useState(true);
  // Mirrors EnrollClassPanel's per-class remaining-sessions validation so
  // the parent can disable the "Hoàn tất" button until every selected
  // class has a valid number entered.
  const [enrollFormValid, setEnrollFormValid] = React.useState(true);

  // Reset the picker state every time the modal opens.
  React.useEffect(() => {
    if (faceEnrollUserId) {
      setEnrollHasSelection(true);
      setEnrollFormValid(true);
    }
  }, [faceEnrollUserId]);

  // M4: Escape key closes face enrollment modal
  React.useEffect(() => {
    if (!faceEnrollUserId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setFaceEnrollUserId(null);
        setFaceEnrollComplete(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [faceEnrollUserId]);

  const handleCreate = async (data: UserInviteCreate) => {
    const payload: UserInviteCreate = {
      ...data,
      center_id: data.center_id ?? currentUserCenterId ?? undefined,
    };
    const created = await userService.create(payload);
    try {
      await fetchUsers();
    } catch (err) {
      console.error("Failed to reload users after create:", err);
    }
    if (created?.id) {
      // Stash the student's name + role so the post-create wizard can greet
      // them and decide whether to offer the "enroll into class" shortcut.
      // Only STUDENTs see that shortcut — staff have no class enrollment.
      pendingCreated.current = {
        fullName: created.full_name ?? "",
        role: (created.role ?? null) as UserRole | null,
      };
      setFaceEnrollUserId(created.id);
    }
  };

  const handleSetPassword = async () => {
    if (!passwordModal || newPassword.length < 8) return;
    setPasswordSubmitting(true);
    setPasswordError(null);
    try {
      await userService.setPassword(passwordModal.userId, newPassword);
      setPasswordSuccess(true);
      setTimeout(() => {
        setPasswordModal(null);
        setNewPassword("");
        setShowNewPassword(false);
        setPasswordSuccess(false);
      }, 1500);
    } catch (err: unknown) {
      setPasswordError(
        err instanceof Error ? err.message : "usersMgmt.setPasswordFailed"
      );
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleToggleConfirm = async () => {
    if (!toggleModal) return;
    setToggleSubmitting(true);
    setToggleError(null);
    try {
      if (toggleModal.currentlyActive) {
        await userService.deactivate(toggleModal.userId);
      } else {
        await userService.activate(toggleModal.userId);
      }
      setToggleModal(null);
      await fetchUsers();
    } catch (err) {
      setToggleError(
        err instanceof Error ? err.message : "usersMgmt.toggleFailed"
      );
    } finally {
      setToggleSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      await userService.deleteUser(deleteModal.userId);
      setDeleteModal(null);
      await fetchUsers();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "usersMgmt.deleteFailed"
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkDeleteModal) return;
    setBulkDeleteSubmitting(true);
    setBulkDeleteError(null);
    const failures: string[] = [];
    for (const userId of bulkDeleteModal.userIds) {
      try {
        await userService.deleteUser(userId);
      } catch {
        failures.push(userId);
      }
    }
    setBulkDeleteSubmitting(false);
    await fetchUsers();
    if (failures.length > 0) {
      setBulkDeleteError(
        t("usersMgmt.bulkDeleteFailed", {
          failed: failures.length,
          total: bulkDeleteModal.userIds.length,
        })
      );
      setBulkDeleteModal({ userIds: failures });
    } else {
      setBulkDeleteModal(null);
    }
  };

  return (
    <div className="mt-5 grid h-full grid-cols-1 gap-5">
      <UserTable
        data={visibleUsers}
        totalUsers={visibleUsers.length}
        loading={loading}
        onCreateClick={() => setIsModalOpen(true)}
        selectedRole={selectedRole}
        onRoleChange={setSelectedRole}
        selectedCenterId={selectedCenterId}
        onCenterChange={setSelectedCenterId}
        centers={centers}
        centersLoading={centersLoading}
        activeFilter={activeFilter}
        onActiveFilterChange={setActiveFilter}
        currentUserRole={currentUserRole}
        roleChangeResetToken={roleChangeResetToken}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSetPassword={(userId) => {
          setPasswordModal({ userId });
          setNewPassword("");
          setShowNewPassword(false);
          setPasswordError(null);
          setPasswordSuccess(false);
        }}
        onViewProfile={(user) => {
          navigate(getUserProfilePath(user.id), {
            state: { from: `${location.pathname}${location.search}` },
          });
        }}
        onUserRoleChange={(userId, newRole) => {
          const user = users.find((u) => u.id === userId);
          setRoleChangeModal({
            userId,
            newRole,
            userName: user?.full_name ?? userId,
          });
          setRoleChangeError(null);
        }}
        onToggleActive={(userId, currentlyActive) => {
          const user = users.find((u) => u.id === userId);
          setToggleModal({
            userId,
            currentlyActive,
            userName: user?.full_name ?? userId,
          });
          setToggleError(null);
        }}
        onDeleteUser={(userId) => {
          const user = users.find((u) => u.id === userId);
          setDeleteModal({ userId, userName: user?.full_name ?? userId });
          setDeleteError(null);
        }}
        onBulkDeleteUsers={(userIds) => {
          setBulkDeleteModal({ userIds });
          setBulkDeleteError(null);
        }}
      />

      <CreateUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
        currentUserRole={currentUserRole}
        presetCenterId={centerIdProp}
      />

      {/* ── Set Password Modal ── */}
      {passwordModal && (
        <div
          className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={(e) =>
            e.target === e.currentTarget && setPasswordModal(null)
          }
        >
          <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-bold text-ink">
              {t("usersMgmt.setPasswordTitle")}
            </h3>
            {passwordSuccess ? (
              <div
                role="status"
                className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-600 dark:bg-green-900/20 dark:text-green-400"
              >
                {t("usersMgmt.setPasswordSuccess")}
              </div>
            ) : (
              <>
                {/* H3: show/hide toggle, same as CreateUserModal */}
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="w-full rounded-xl border border-line bg-surface px-4 py-2.5 pr-16 text-sm text-ink outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder={t("usersMgmt.newPasswordPlaceholder")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-0.5 text-xs text-ink-muted hover:text-brand-500"
                  >
                    {showNewPassword
                      ? t("usersMgmt.create.hidePassword")
                      : t("usersMgmt.create.showPassword")}
                  </button>
                </div>
                {/* M3: role="alert" so screen readers announce errors */}
                {passwordError && (
                  <div
                    role="alert"
                    className="mt-2 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400"
                  >
                    {t(passwordError)}
                  </div>
                )}
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setPasswordModal(null)}
                    className="cursor-pointer rounded-xl border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                  >
                    {t("action.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSetPassword}
                    disabled={passwordSubmitting || newPassword.length < 8}
                    className="cursor-pointer rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
                  >
                    {passwordSubmitting
                      ? t("action.saving")
                      : t("action.confirm")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Role Change Confirmation Modal ── */}
      {roleChangeModal && (
        <div
          className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !roleChangeSubmitting) {
              setRoleChangeModal(null);
              setRoleChangeResetToken((n) => n + 1);
            }
          }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-bold text-ink">
              {t("usersMgmt.roleChange.title")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {renderBold(t("usersMgmt.roleChange.confirm"), {
                name: roleChangeModal.userName,
                role: ROLE_LABEL_KEYS[roleChangeModal.newRole as UserRole]
                  ? t(ROLE_LABEL_KEYS[roleChangeModal.newRole as UserRole])
                  : roleChangeModal.newRole,
              })}
            </p>
            {/* M3: role="alert" */}
            {roleChangeError && (
              <div
                role="alert"
                className="mt-3 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400"
              >
                {t(roleChangeError)}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setRoleChangeModal(null);
                  setRoleChangeResetToken((n) => n + 1);
                }}
                disabled={roleChangeSubmitting}
                className="cursor-pointer rounded-xl border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              >
                {t("action.cancel")}
              </button>
              <button
                type="button"
                disabled={roleChangeSubmitting}
                onClick={async () => {
                  setRoleChangeSubmitting(true);
                  setRoleChangeError(null);
                  try {
                    await userService.updateUserRole(
                      roleChangeModal.userId,
                      roleChangeModal.newRole
                    );
                    setRoleChangeModal(null);
                    await fetchUsers();
                  } catch (err) {
                    setRoleChangeError(
                      err instanceof Error
                        ? err.message
                        : "usersMgmt.roleChange.failed"
                    );
                  } finally {
                    setRoleChangeSubmitting(false);
                  }
                }}
                className="cursor-pointer rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {roleChangeSubmitting
                  ? t("action.saving")
                  : t("action.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── C1: Toggle Active Confirmation Modal ── */}
      {toggleModal && (
        <div
          className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !toggleSubmitting)
              setToggleModal(null);
          }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl">
            <h3 className="mb-3 text-lg font-bold text-ink">
              {toggleModal.currentlyActive
                ? t("usersMgmt.toggle.deactivateTitle")
                : t("usersMgmt.toggle.activateTitle")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {toggleModal.currentlyActive ? (
                <>
                  {renderBold(t("usersMgmt.toggle.deactivateBody"), {
                    name: toggleModal.userName,
                  })}
                </>
              ) : (
                <>
                  {renderBold(t("usersMgmt.toggle.activateBody"), {
                    name: toggleModal.userName,
                  })}
                </>
              )}
            </p>
            {/* M3: role="alert" */}
            {toggleError && (
              <div
                role="alert"
                className="mt-3 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400"
              >
                {t(toggleError)}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setToggleModal(null)}
                disabled={toggleSubmitting}
                className="cursor-pointer rounded-xl border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              >
                {t("action.cancel")}
              </button>
              <button
                type="button"
                onClick={handleToggleConfirm}
                disabled={toggleSubmitting}
                className={`cursor-pointer rounded-xl px-4 py-2 text-sm font-medium text-white disabled:opacity-60 ${
                  toggleModal.currentlyActive
                    ? "bg-orange-500 hover:bg-orange-600"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {toggleSubmitting
                  ? t("action.saving")
                  : toggleModal.currentlyActive
                  ? t("usersMgmt.table.deactivate")
                  : t("usersMgmt.table.activate")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── C1: Delete Confirmation Modal ── */}
      {deleteModal && (
        <div
          className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteSubmitting)
              setDeleteModal(null);
          }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl">
            <h3 className="mb-3 text-lg font-bold text-ink">
              {t("usersMgmt.delete.title")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {renderBold(t("usersMgmt.delete.body"), {
                name: deleteModal.userName,
              })}
            </p>
            {/* M3: role="alert" */}
            {deleteError && (
              <div
                role="alert"
                className="mt-3 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400"
              >
                {t(deleteError)}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteModal(null)}
                disabled={deleteSubmitting}
                className="cursor-pointer rounded-xl border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              >
                {t("action.cancel")}
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteSubmitting}
                className="cursor-pointer rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deleteSubmitting
                  ? t("usersMgmt.delete.deleting")
                  : t("usersMgmt.table.deleteUser")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Delete Confirmation Modal ── */}
      {bulkDeleteModal && (
        <div
          className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !bulkDeleteSubmitting)
              setBulkDeleteModal(null);
          }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl">
            <h3 className="mb-3 text-lg font-bold text-ink">
              {t("usersMgmt.bulkDelete.title", {
                count: bulkDeleteModal.userIds.length,
              })}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t("usersMgmt.bulkDelete.body")}
            </p>
            {bulkDeleteError && (
              <div
                role="alert"
                className="mt-3 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400"
              >
                {t(bulkDeleteError)}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setBulkDeleteModal(null)}
                disabled={bulkDeleteSubmitting}
                className="cursor-pointer rounded-xl border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              >
                {t("action.cancel")}
              </button>
              <button
                type="button"
                onClick={handleBulkDeleteConfirm}
                disabled={bulkDeleteSubmitting}
                className="cursor-pointer rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
              >
                {bulkDeleteSubmitting
                  ? t("usersMgmt.delete.deleting")
                  : t("usersMgmt.bulkDelete.submit", {
                      count: bulkDeleteModal.userIds.length,
                    })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Face Registration + Class Picker Modal ── */}
      {faceEnrollUserId &&
        (() => {
          // The right-hand class picker only appears for STUDENTs once
          // face enrollment is complete AND the user has at least one
          // class/session selected. All other states keep the modal in
          // single-column mode (like the original face-enroll modal).
          const showClassPicker =
            pendingCreated.current.role === "STUDENT" &&
            faceEnrollComplete &&
            enrollHasSelection;
          return (
            <div
              className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
              onClick={(e) => {
                if (e.target === e.currentTarget && !enrollSaving) {
                  setFaceEnrollUserId(null);
                  setFaceEnrollComplete(false);
                }
              }}
            >
              <div
                className={`flex max-h-[90vh] w-full ${
                  showClassPicker ? "max-w-5xl" : "max-w-lg"
                } flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl`}
              >
                <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-3">
                  <div>
                    <h3 className="text-lg font-bold text-ink">
                      {t("firstLogin.stepFace")}
                    </h3>
                    {showClassPicker && (
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {t("usersMgmt.faceEnroll.subtitle", {
                          name:
                            pendingCreated.current.fullName ||
                            t("enum.role.STUDENT"),
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFaceEnrollUserId(null);
                      setFaceEnrollComplete(false);
                    }}
                    disabled={enrollSaving}
                    aria-label={t("action.close")}
                    className="cursor-pointer rounded-lg p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    ✕
                  </button>
                </div>

                {/*
              Body area: header + footer are shrink-0, this region fills
              the remaining vertical space and scrolls independently.
              When split into 2 columns, each column scrolls on its own
              so a long session list can't push the face-enroll card
              (or the footer) out of the viewport.
            */}
                <div
                  className={`flex min-h-0 flex-1 flex-col ${
                    showClassPicker ? "md:flex-row" : ""
                  }`}
                >
                  {/* Trái: đăng ký khuôn mặt */}
                  <div
                    className={`min-h-0 flex-1 overflow-y-auto p-6 dark:border-line ${ showClassPicker ? "border-b border-gray-200 md:max-w-md md:border-b-0 md:border-r"
                        : ""
                    }`}
                  >
                    <FaceEnrollCard
                      userId={faceEnrollUserId}
                      canModifyExisting
                      onAllComplete={() => setFaceEnrollComplete(true)}
                    />
                  </div>

                  {/* Phải: chỉ hiện khi role=STUDENT + đã đăng ký khuôn mặt xong */}
                  {showClassPicker && (
                    <div className="min-h-0 flex-1 overflow-y-auto">
                      <EnrollClassPanel
                        ref={enrollPanelRef}
                        studentId={faceEnrollUserId}
                        centerId={currentUserCenterId}
                        onSelectionChange={setEnrollHasSelection}
                        onValidityChange={setEnrollFormValid}
                      />
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center justify-between border-t border-line px-6 py-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFaceEnrollUserId(null);
                      setFaceEnrollComplete(false);
                    }}
                    disabled={enrollSaving}
                    className="cursor-pointer rounded-xl border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300"
                  >
                    {faceEnrollComplete
                      ? t("action.close")
                      : t("usersMgmt.faceEnroll.skip")}
                  </button>
                  <button
                    type="button"
                    disabled={enrollSaving || !enrollFormValid}
                    onClick={async () => {
                      if (!faceEnrollUserId) return;
                      const {
                        classIds,
                        sessionIds,
                        remainingSessionsByClassId,
                      } = enrollPanelRef.current?.getSelections() ?? {
                        classIds: new Set<string>(),
                        sessionIds: new Set<string>(),
                        remainingSessionsByClassId: new Map<string, string>(),
                      };
                      const missingCount = Array.from(classIds).filter(
                        (cid) => {
                          const raw = remainingSessionsByClassId.get(cid) ?? "";
                          if (raw.trim() === "") return true;
                          const num = Number(raw);
                          return !Number.isFinite(num) || num < 0;
                        }
                      ).length;
                      if (missingCount > 0) {
                        addNotification(
                          t("usersMgmt.faceEnroll.missingRemaining", {
                            count: missingCount,
                          }),
                          "error"
                        );
                        return;
                      }
                      setEnrollSaving(true);
                      try {
                        const studentName =
                          pendingCreated.current.fullName ||
                          t("usersMgmt.faceEnroll.studentFallback");
                        // One request enrolls into every selected class and
                        // adds to every selected session — backend handles
                        // each item independently (best-effort), so one
                        // failure (e.g. already enrolled) can't block the
                        // rest.
                        const result = await classMgmtService.batchEnroll(
                          faceEnrollUserId,
                          {
                            classes: Array.from(classIds).map((cid) => {
                              const remainingRaw = remainingSessionsByClassId
                                .get(cid)
                                ?.trim();
                              const remaining =
                                remainingRaw !== undefined &&
                                remainingRaw !== "" &&
                                Number.isFinite(Number(remainingRaw)) &&
                                Number(remainingRaw) >= 0
                                  ? Number(remainingRaw)
                                  : undefined;
                              return {
                                class_id: cid,
                                remaining_sessions: remaining,
                              };
                            }),
                            session_ids: Array.from(sessionIds),
                          }
                        );
                        const enrolledCount = result.classes.filter(
                          (item) => item.success
                        ).length;
                        const syncedCount = result.sessions.filter(
                          (item) => item.success
                        ).length;
                        // Pick a whole sentence per case rather than joining fragments.
                        const toastKey =
                          enrolledCount > 0 && syncedCount > 0
                            ? "usersMgmt.faceEnroll.addedClassesAndSessions"
                            : enrolledCount > 0
                            ? "usersMgmt.faceEnroll.addedClasses"
                            : syncedCount > 0
                            ? "usersMgmt.faceEnroll.addedSessions"
                            : null;
                        if (toastKey) {
                          addNotification(
                            t(toastKey, {
                              name: studentName,
                              classes: enrolledCount,
                              sessions: syncedCount,
                            }),
                            "success"
                          );
                        }
                        setFaceEnrollUserId(null);
                        setFaceEnrollComplete(false);
                      } finally {
                        setEnrollSaving(false);
                      }
                    }}
                    className="cursor-pointer rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {enrollSaving
                      ? t("action.saving")
                      : t("usersMgmt.faceEnroll.finish")}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default UsersManagement;
